import {
    FEATURE_FLAG_NAMES,
    FeatureFlags,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { SessionService } from '@thoughtspot/session-service';
import _ from 'lodash';

export const CALLOSUM_TRACE_ID_HEADER = 'X-Callosum-Trace-Id';

// LD flag wins when delivered; falls back to cluster gate so existing clusters see no regression.
export const isQueryCancellationEnabled = (
    sessionService: SessionService,
    featureFlags: FeatureFlags,
): boolean => {
    if (
        !_.isNil(featureFlags?.[FEATURE_FLAG_NAMES.ENABLE_QUERY_CANCELLATION])
    ) {
        return isFeatureEnabled(FEATURE_FLAG_NAMES.ENABLE_QUERY_CANCELLATION);
    }
    return sessionService.isQueryCancellationEnabled();
};

// Inline: sendBeacon fires synchronously on pagehide when Apollo is already torn down.
const DATA_CANCEL_QUERY = `mutation DataCancel($cancelIds: [String!]!) {
  data_cancel(cancelIds: $cancelIds)
}`;

export const sendDataCancelBeacon = (cancelIds: string[]): boolean => {
    if (
        !cancelIds?.length ||
        typeof navigator === 'undefined' ||
        typeof navigator.sendBeacon !== 'function'
    ) {
        return false;
    }

    try {
        const body = JSON.stringify({
            operationName: 'DataCancel',
            variables: { cancelIds },
            query: DATA_CANCEL_QUERY,
        });
        const blob = new Blob([body], { type: 'application/json' });
        return navigator.sendBeacon('/prism/?op=DataCancel', blob);
    } catch {
        return false;
    }
};

export const createAnswerDataQueryCancelId = (): string => {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`;
};

export const isAbortQueryError = (queryError: any): boolean => {
    if (!queryError) {
        return false;
    }

    if (queryError.networkError?.name === 'AbortError') {
        return true;
    }

    const errorMessage = (
        queryError.message ||
        queryError.errorText ||
        queryError.networkError?.message ||
        ''
    ).toLowerCase();

    return errorMessage.includes('signal is aborted');
};

export const getCancelableDataQueryContext = (
    signal?: AbortSignal,
    cancelId?: string,
) => ({
    ...(signal && !signal.aborted
        ? {
              fetchOptions: {
                  signal,
              },
          }
        : {}),
    ...(cancelId
        ? {
              headers: {
                  [CALLOSUM_TRACE_ID_HEADER]: cancelId,
              },
          }
        : {}),
});
